<script>

import {axios} from '../../mixins';
import Geography from '../Geography/Geography.vue';
import {Alerts} from '../../mixins/Alerts.js';
import MapBox from './../Geography/MapBox.vue';
import Modal from "../Modal.vue";

export default {

  name: 'FieldsForm',

  mixins: [axios],

  components: {Geography, MapBox, Modal},

  props: {
    resource: {
      required: true,
      type: Object,
    },
    locked: {
      required: false,
      type: Boolean,
      default: false,
    },
    options: {
      required: false,
      type: Object,
      default: function () {
        return {};
      },
    },
    mapboxToken: {
      required: true,
      type: String,
    },
    esriToken: {
      required: true,
      type: String,
    },
    alerts: {
      required: false,
      type: Alerts,
    },
    digifarmToken: {
      required: true,
      type: String,
    },
    farmBoundaries: {
      required: false,
      type: Object,
      default: null,
    },
    zipCode: {
      required: false,
      type: String,
    },
    farmAddress: {
      required: false,
      type: String,
    },
    farmId: {
      required: false,
      type: Number
    },
    // Updated via parent to manage error modal
    boundaryError: {
      required: false,
      type: Boolean
    },
    // Controlled by tenant setting
    selectEnabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  watch: {
    resource() {
      this.resourceForm = {...this.resource};
      this.boundaryUpdating = false
    }
  },

  data() {
    return {
      resourceForm: {},
      boundaryUpdating: false,
      updateFieldYears: false,
      editableClasses: 'bg-secondary italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none text-md',
      editableClassesSelect: 'bg-secondary italic rounded border border-grey-lighter w-full py-2 px-3 leading-tight focus:outline-none focus:ring',
      fieldYears: [],
      ipmText: 'Loading...',
      boundaries: null,
      submitting: false,
      confirm: false,
      boundaryToolbarHelp: [
        'Click a field to enter vertex edit mode.',
        'Use the Select Vertices tool to drag a box around vertices.',
        'Hold Shift and click individual vertices to add or remove them from the selection.',
        'Drag any selected vertex to move the whole selected group.',
        'Press Delete or Backspace to remove selected vertices.',
        'If polygons were combined, use Uncombine Features before Select Vertices.',
      ],
    };
  },

  computed: {
    isCreating() {
      return this.resource.id === null
    }
  },

  mounted() {
    window.addEventListener("keydown", this.keyListener);
    window.setInterval(() => {
      // Add checks to manage popups and loading indicators
      this.checkForErrors()
      this.checkForSubmission()
    }, 1000)

    this.resourceForm = {...this.resource};
    this.$nextTick(function () {
      if (this.resource.id != null) {
        this.getOtherBoundaries()
      }
    })
  },

  created() {
    this.boundaries = this.farmBoundaries;
    this.sortFieldTypes()
  },

  methods: {
    indicatorMetaFor(attribute) {
      const metaRoot = (this.options && this.options.indicatorMeta) ? this.options.indicatorMeta[attribute] : null;
      if (!metaRoot) return null;
      return metaRoot.default || null;
    },
    indicatorIsActive(attribute) {
      const meta = this.indicatorMetaFor(attribute);
      if (!meta) return true;
      return meta.is_active !== false;
    },
    indicatorLabel(attribute, fallback) {
      const meta = this.indicatorMetaFor(attribute);
      const label = meta && meta.label ? meta.label : null;
      return label && label.length ? label : fallback;
    },
    submitResource() {
      this.submitting = true
      let dataToSubmit = {...this.resource}
      dataToSubmit.data = this.resourceForm

      // get boundary data from the map
      let boundaryData = this.$refs['boundary-input'].getValue()
      dataToSubmit.data.boundary = boundaryData.boundary
      dataToSubmit.data.size = boundaryData.size
      dataToSubmit.data.centroid = boundaryData.center

      if (this.isCreating) {
        dataToSubmit.data.update_field_years = false
      } else {
        dataToSubmit.data.update_field_years = this.updateFieldYears
      }

      dataToSubmit.type = 'fields'

      if (this.isCreating) {
        delete dataToSubmit.data.updateFieldYears;

        this.$emit('store', dataToSubmit)
      } else {
        this.$emit('update', dataToSubmit)
      }
    },

    checkForSubmission() {
      if (!this.alerts.isEmpty()) {
        this.submitting = false
      }
    },

    checkForErrors() {
      if (this.boundaryError) {
        this.$refs.boundaryErrorModal.open();
      }
    },

    deleteResource() {
      this.resource.type = 'fields'
      this.$emit('delete', this.resource)
    },

    // Get boundary data from Geography.vue
    updateBoundary(data) {
      this.resourceForm.size = data.size
      this.resourceForm.centroid = data.centroid
      this.boundaryUpdating = true
      this.updateFieldYears = true
    },

    confirmClose(cancel) {
      this.confirm = false;
      if (!cancel) {
        this.deleteResource()
      }
    },

    getHelpText() {
      this.get('/ipm').then((response) => {
        this.ipmText = response.data.text;
      })
    },

    getOtherBoundaries() {
      this.get('fields/' + this.resource.id + '/farm-boundary?id=' + this.resource.id + '&farm_id=' + this.resource.farm_id).then((response) => {
        this.boundaries = response.data.boundary
      })
    },

    closeBoundaryModal() {
      this.$parent.boundaryError = false;
      this.$refs.boundaryErrorModal.close()
    },

    // Custom sort requested by SWOF
    sortFieldTypes() {
      // Check if list is unsorted, first
      if (this.options.field_type[397]) {
        this.options.field_type = {
          0: Object.fromEntries(Object.entries(this.options.field_type[397])),
          1: Object.fromEntries(Object.entries(this.options.field_type[399])),
          2: Object.fromEntries(Object.entries(this.options.field_type[396])),
          3: Object.fromEntries(Object.entries(this.options.field_type[477])),
          4: Object.fromEntries(Object.entries(this.options.field_type[400])),
        }
      }
    }
  },
};

</script>

<template>
  <form @submit.prevent="submitResource" method="post">
    <div class="p-4 flex flex-row w-full">

      <div class="flex flex-1 flex-col mr-2">
        <div v-if="!this.isCreating" class="text-2xl font-bold py-3 border-b-2 border-grey">
            Field{{ isCreating ? '' : ': ' + resource.name }}
        </div>
        <div v-if="isCreating">
          <slot name="create-field"></slot>
        </div>
        <div class="py-4 flex flex-col">

          <!-- Farm ID -->
          <div>
            <label for="farm_id" hidden>Farm</label>
            <input type="number" name="farm_id" id="farm_id" :value="resourceForm.farm_id" hidden>
          </div>

          <!-- NAME -->
          <div class="flex flex-row flex-wrap flex-1 mb-3">
            <label for="name" class="font-semibold">Field Name*</label>
            <input name="name"
                   id="name"
                   v-model="resourceForm.name"
                   :disabled="locked"
                   :class="editableClasses"
                   type="text">
            <div class="text-red">
              {{ alerts.getError('name') }}
            </div>
          </div>

          <!-- TILE DRAINAGE -->
          <div class="flex flex-col flex-wrap flex-1 mb-3">
            <div class="font-semibold">
              Does tile drainage exist in 50% or more of this field?
            </div>
            <div class="flex flex-row">
              <div class="flex flex-row flex-wrap flex-1 my-1">
                <label for="has_tile_drainage" hidden>Tile Drainage</label>
                <select name="has_tile_drainage"
                        id="has_tile_drainage"
                        v-model="resourceForm.has_tile_drainage"
                        :class="editableClassesSelect"
                        :disabled="locked">
                  <option :value="`Yes`">Yes</option>
                  <option :value="`No`">No</option>
                  <option :value="`Unsure`">Unsure</option>
                </select>
                <div class="text-red">
                  {{ alerts.getError('has_tile_drainage') }}
                </div>
              </div>
            </div>
          </div>

          <!-- TILE DRAINAGE QUESTIONS -->
          <div v-if="resourceForm.has_tile_drainage==='Yes'">

            <div class="flex flex-row flex-wrap flex-1 my-1">
              <label for="drainage_depth" class="font-semibold">What is the approximate depth of the drainage system in
                feet?</label>
              <input name="drainage_depth"
                     id="drainage_depth"
                     v-model="resourceForm.drainage_depth"
                     :disabled="locked"
                     :class="editableClasses"
                     type="number"
                     min="0">
            </div>
            <div class="text-red">
              {{ alerts.getError('drainage_depth') }}
            </div>

            <div class="flex flex-col flex-wrap flex-1 my-1">
              <div class="font-semibold">
                Is there an accessible drainage outlet from this field?
              </div>
              <div class="flex flex-row">
                <div class="flex flex-row flex-wrap flex-1 my-1">
                  <label for="drainage_has_accessible_outlet" hidden>Accessible Outlet</label>
                  <select name="drainage_has_accessible_outlet"
                          id="drainage_has_accessible_outlet"
                          v-model="resourceForm.drainage_has_accessible_outlet"
                          :class="editableClassesSelect"
                          :disabled="locked">
                    <option :value="`Yes`">Yes</option>
                    <option :value="`No`">No</option>
                    <option :value="`Unknown`">Unknown</option>
                  </select>
                  <div class="text-red">
                    {{ alerts.getError('drainage_has_accessible_outlet') }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-col flex-wrap flex-1 mb-3">
            <div class="font-semibold">
              Are you practicing Integrated Pest Management (IPM) principles in this field?*
              <button type="button" @click="this.$refs.helpModal.open(); this.getHelpText()"><span class="fas fa-question-circle hover:cursor-pointer"></span></button>
            </div>
            <div class="flex flex-row">
              <div class="flex flex-row flex-wrap flex-1 my-1">
                <select name="ipm_principles_used"
                        id="ipm_principles_used"
                        v-model="resourceForm.ipm_principles_used"
                        :class="editableClassesSelect"
                        :disabled="locked">
                  <option :value="true">Yes</option>
                  <option :value="false">No</option>
                  <option :value="null">Unsure</option>
                </select>
              </div>
            </div>
            <div class="text-red">
              {{ alerts.getError('ipm_principles_used') }}
            </div>
          </div>

          <!-- FIELD YEAR QUESTIONS -->
          <!-- FIELD TYPE -->
          <div class="flex flex-col flex-wrap flex-1 mb-3" v-if="indicatorIsActive('field_type')">
            <div class="font-semibold">
              {{ indicatorLabel('field_type', 'Field Type*') }}
            </div>
            <div class="flex flex-row">
              <div class="flex flex-row flex-wrap flex-1 my-1">
                <select name="field_type"
                        id="field_type"
                        v-model="resourceForm.field_type"
                        :class="editableClassesSelect"
                        :disabled="locked">
                  <option :value="null">Select...</option>
                  <option v-for="option in options.field_type" :value="option.value">{{ option.label }}</option>
                </select>
                <div class="text-red">
                  {{ alerts.getError('field_type') }}
                </div>
              </div>
            </div>
          </div>

          <div class="text-sm flex flex-row-reverse justify-between">
            <div v-if="this.submitting"
                 class="button button-wide bg-blue text-white hover:bg-blue-dark mt-2 font-bold">
              <span class="fas fa-spinner-third fa-spin"></span>
            </div>

            <button
                v-show="!this.submitting"
                :disabled="this.submitting || this.locked"
                class="button button-wide bg-blue text-white hover:bg-blue-dark focus:bg-blue-darker mt-2 font-bold disabled:cursor-not-allowed"
                type="submit" name="field-submit">
              <span class="mr-1"><i class="fas fa-floppy-disk"/></span>
              <span>Save</span>
            </button>

            <button
                v-if="!this.isCreating"
                :disabled="this.submitting || this.locked"
                class="button button-wide bg-red text-white hover:bg-red-dark focus:bg-red-darker mt-2 font-bold disabled:cursor-not-allowed"
                type="button"
                @click="this.confirm = true">
              <span class="mr-1"><i class="fas fa-trash"/></span>
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Boundary map -->
      <div class="flex flex-col w-3/4 ml-2">
        <div>
          <!-- BOUNDARY -->
          <div class="flex flex-col flex-wrap flex-1 mb-3">
            <Geography
                name="boundary"
                id="field-boundary"
                :source="resourceForm.boundary && typeof (resourceForm.boundary) === 'object'? resourceForm.boundary : null"
                :boundaries="this.boundaries"
                upload-url="/boundary/upload"
                ref="boundary-input"
                :mapbox-token="mapboxToken"
                :esri-token="esriToken"
                @updateGeography="updateBoundary"
                :digifarm-token="digifarmToken"
                :farmAddress="this.farmAddress"
                :farmId="this.farmId"
                :zipCode="this.zipCode"
                :selectEnabled="this.selectEnabled && this.isCreating"
                :toolbar-help-items="boundaryToolbarHelp"
            >

            </Geography>
            <div class="flex flex-col flex-wrap flex-1 my-1" v-if="boundaryUpdating && !isCreating">
              <div class="font-semibold">
                Would you like to update all crop years with this boundary?
              </div>
              <div class="flex flex-row">
                <label class="w-full">
                  <input v-model="updateFieldYears" type="checkbox" name="update_field_years"
                         value="1" class="mr-1">
                  Yes
                </label>
              </div>
            </div>
            <div class="text-red text-lg">
              {{ alerts.getError('size') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>

  <div v-if="this.confirm" class="fixed flex justify-center items-center top-0 bottom-0 left-0 right-0" @click="this.confirm = false">
    <div class="bg-primary p-4 w-1/6 text-center tile-shadow rounded" @click.stop="">
      <div>Are you sure you wish to delete this field?</div>
      <div class="flex justify-between">
        <button class="button button-wide text-grey-dark hover:bg-grey-lightest focus:bg-grey-lighter mt-2 font-bold"
                @click="confirmClose(true)">
          Cancel
        </button>
        <button class="button button-wide text-white bg-red hover:bg-red-dark focus:bg-red-darker mt-2 font-bold"
                @click="confirmClose(false)">
          Yes, Delete
        </button>
      </div>
    </div>
  </div>

  <Modal ref="helpModal" :small="true">
    <div class="w-full bg-primary p-2 overflow-auto h-96" aria-orientation="vertical" aria-labelledby="options-menu">
      <div v-html="this.ipmText"></div>
    </div>
    <template #toggle="{open}"></template>
    <template #footer="{close}">
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
                <button type="button"
                        class="button text-white font-bold bg-secondary hover:bg-primary focus:bg-tertiary mt-2 mr-2"
                        v-on:click="close">
                  Close
                </button>
              </span>
    </template>
  </Modal>

  <!-- BOUNDARY ERROR MODAL !-->
  <modal ref="boundaryErrorModal" :small="true">
    <div class="w-full p-2">
      <span v-html="this.alerts.getError('boundary')"/>
    </div>
    <template #footer="{close}">
      <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button type="button"
                class="px-6 py-1 rounded-sm text-white inline-block shadow-lg bg-green hover:bg-green-dark focus:bg-green-darker mt-2 font-bold mr-2"
                v-on:click="this.closeBoundaryModal()">
          I understand
        </button>
      </span>
    </template>
  </modal>

</template>
